import type { TradingLink } from './api'
import { useEffect, useState } from 'react'
import { fetchSportTradingLinks, updateSportTradingLinks } from './api'
import { formatRelative, formatTsUtc } from './time'

interface Props {
  sportId: number
  // Trading+ — same gate as the rest of the /trading page.
  canEdit: boolean
}

type Mode = 'view' | 'edit'

// LINKS — per-sport quick-reference buttons with customizable text, grouped
// under trader-named subsections (successor to the Trading Bible's "Useful
// Links" sheet). Same self-contained fetch + edit/save lifecycle as
// TradingEmailsPanel; the whole list saves in one full-replace RPC call.
export default function TradingLinksPanel({ sportId, canEdit }: Props) {
  const [rows, setRows] = useState<TradingLink[]>([])
  const [baseline, setBaseline] = useState<TradingLink[]>([])
  const [meta, setMeta] = useState<{ updated_at: string | null, updated_by: string | null }>({ updated_at: null, updated_by: null })
  const [mode, setMode] = useState<Mode>('view')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setMode('view')
    setMsg(null)
    setLoading(true)
    fetchSportTradingLinks(sportId)
      .then((data) => {
        if (cancelled) return
        setRows(data.links)
        setBaseline(data.links)
        setMeta({ updated_at: data.updated_at, updated_by: data.updated_by })
      })
      .catch((e) => {
        if (!cancelled) setMsg({ kind: 'err', text: (e as Error).message })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sportId])

  function cancel() {
    setRows(baseline)
    setMode('view')
    setMsg(null)
  }

  function setRow(i: number, patch: Partial<TradingLink>) {
    setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  }

  function addRow() {
    // Prefill the section from the last row — entering several links into
    // one subsection shouldn't mean retyping its name.
    setRows(prev => [...prev, { group_name: prev[prev.length - 1]?.group_name ?? '', label: '', url: '' }])
  }

  async function save() {
    const cleaned: TradingLink[] = []
    for (const r of rows) {
      const label = r.label.trim()
      let url = r.url.trim()
      // Fully blank rows (an added row never filled in) drop silently.
      if (!label && !url) continue
      if (!label || !url) {
        setMsg({ kind: 'err', text: 'Every link needs both button text and a URL.' })
        return
      }
      // Scheme-less entries get https:// — traders paste bare domains.
      if (!/^https?:\/\//i.test(url)) url = `https://${url}`
      try {
        void new URL(url)
      }
      catch {
        setMsg({ kind: 'err', text: `Not a valid URL: ${r.url}` })
        return
      }
      cleaned.push({ group_name: r.group_name.trim(), label, url })
    }
    setBusy(true)
    setMsg(null)
    try {
      await updateSportTradingLinks(sportId, cleaned)
      const saved = await fetchSportTradingLinks(sportId)
      setRows(saved.links)
      setBaseline(saved.links)
      setMeta({ updated_at: saved.updated_at, updated_by: saved.updated_by })
      setMode('view')
      setMsg({ kind: 'ok', text: 'Saved links.' })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  // Group by subsection in first-appearance order; '' renders unheaded.
  const groups: { name: string, links: TradingLink[] }[] = []
  for (const r of rows) {
    const name = r.group_name.trim()
    const g = groups.find(x => x.name === name)
    if (g) g.links.push(r)
    else groups.push({ name, links: [r] })
  }

  const readOnly = mode === 'view'

  return (
    <div className="ad-editor-section">
      <div className="ad-section-head">
        <h3 className="b-mono">LINKS</h3>
        {canEdit && !loading && (
          <div className="ad-section-actions">
            {readOnly
              ? (
                  <button type="button" className="b-btn b-btn-tiny" onClick={() => setMode('edit')}>
                    EDIT
                  </button>
                )
              : (
                  <>
                    <button type="button" className="b-btn-ghost b-btn-tiny" onClick={cancel} disabled={busy}>
                      CANCEL
                    </button>
                    <button type="button" className="b-btn b-btn-primary b-btn-tiny" onClick={save} disabled={busy}>
                      {busy ? 'SAVING…' : 'SAVE'}
                    </button>
                  </>
                )}
          </div>
        )}
      </div>

      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>{msg.text}</p>
      )}

      {loading
        ? <p className="b-mono-dim b-tiny">Loading…</p>
        : readOnly
          ? rows.length === 0
            ? <p className="b-mono-dim b-tiny">— empty —</p>
            : (
                <div className="ad-links-groups">
                  {groups.map(g => (
                    <div key={g.name || '(ungrouped)'} className="ad-links-group">
                      {g.name && <p className="b-mono-dim b-tiny ad-links-group-title">{g.name.toUpperCase()}</p>}
                      <div className="ad-links-row">
                        {g.links.map(l => (
                          <a
                            key={`${l.label}-${l.url}`}
                            className="ad-link-btn"
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {l.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
          : (
              <div className="ad-links-edit">
                {rows.map((r, i) => (
                  /* Rows are positional while editing — index keys are fine
                     since edits mutate in place and deletes splice. */
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="ad-links-edit-row">
                    <input
                      type="text"
                      className="ad-input"
                      value={r.group_name}
                      onChange={e => setRow(i, { group_name: e.target.value })}
                      placeholder="Section (optional)…"
                    />
                    <input
                      type="text"
                      className="ad-input"
                      value={r.label}
                      onChange={e => setRow(i, { label: e.target.value })}
                      placeholder="Button text…"
                    />
                    <input
                      type="text"
                      className="ad-input"
                      value={r.url}
                      onChange={e => setRow(i, { url: e.target.value })}
                      placeholder="https://…"
                    />
                    <button
                      type="button"
                      className="ad-list-remove"
                      onClick={() => setRows(prev => prev.filter((_, idx) => idx !== i))}
                      disabled={busy}
                      aria-label={`Remove ${r.label || 'link'}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div>
                  <button type="button" className="b-btn b-btn-tiny" onClick={addRow} disabled={busy}>
                    + ADD LINK
                  </button>
                </div>
              </div>
            )}

      {readOnly && meta.updated_at && (
        <p className="ad-trading-note-meta b-mono-dim b-tiny">
          updated
          {' '}
          <span title={formatTsUtc(meta.updated_at)}>{formatRelative(meta.updated_at)}</span>
          {meta.updated_by && ` by ${meta.updated_by}`}
        </p>
      )}
    </div>
  )
}
